import { Box } from "@chakra-ui/layout";
import { Spinner } from "@chakra-ui/react"
import {  memo, ReactEventHandler, useEffect, useState } from "react"
import { RoomBranding } from "src/recoil/atoms/room/types"
import { useIsVideoMimeType } from "./utils/useIsVideo"
import { ChatCloseIcon } from "./_chatCloseIcon"
import useSizeImage from "./utils/useSizeImage"
import { IoIosVideocam } from "react-icons/io";
import { useSetRecoilState } from "recoil";
import { chatAttachmentHeightSelector } from "src/recoil/atoms/chats";

type Props = {
  roomStyles: RoomBranding
  borderColor: string
  attachment: {
    data: string
    mimeType: string
  }
  removeAttachment: () => void
  saving: boolean
}

function _ChatAttachmentPreview({
  roomStyles,
  borderColor,
  attachment,
  removeAttachment,
  saving,
}: Props) {

  const isVideo = useIsVideoMimeType(attachment.mimeType)
  const setChatAttachmentHeight = useSetRecoilState(chatAttachmentHeightSelector)
  const [aspectRatio, setAspectRatio] = useState<`${number}/${number}`>("1/1")
  const {width, height} = useSizeImage({
    width: 207,
    height: 207
  }, {
    aspectRatio
  })

  const onLoad: ReactEventHandler<HTMLImageElement> = function(e) {
    const { offsetWidth: w, offsetHeight: h } = e.currentTarget
    setAspectRatio(`${w}/${h}`)
  }

  const onLoadVideo: ReactEventHandler<HTMLVideoElement> = function(e) {
    const w = e.currentTarget.videoWidth, h = e.currentTarget.videoHeight;
    setAspectRatio(`${w}/${h}`)
  }

  useEffect(() => {
    // We need height for handling UI
    setChatAttachmentHeight(height)
  }, [height])

  return (
    <Box
      pos="relative"
      margin="0 auto"
      borderColor={borderColor}
      zIndex={10000000}
      paddingY="1em"
      boxSizing="border-box"
    >
      <Box pos="relative" maxWidth={width} maxHeight={height} margin="0 auto">
        {isVideo ? (
          <>
            <Box
              pos="absolute"
              top={0}
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="3rem"
              fontWeight="bold"
              as="span"
            >
              <Box as="span" fontSize="2.1rem" mr=".25rem">
                MOV
              </Box>
              <IoIosVideocam />
            </Box>
            <video
              src={attachment.data}
              style={{
                position: "relative",
                borderRadius: "4px",
                overflow: "hidden",
                width: "100%",
                height: "100%"
              }}
              onLoadedMetadata={onLoadVideo}
              controls={false}
            />
          </>
        ) : (
          <img
            src={attachment.data}
            style={{
              borderRadius: "4px",
              overflow: "hidden",
              width: "100%",
              height: "100%"
            }}
            onLoad={onLoad}
          />
        )}
        <ChatCloseIcon
          styleschat={roomStyles}
          isMobile={false}
          callback={removeAttachment}
          id={"AttachmentPreview"}
          isLarge
        />
        {saving && (
          <Box
            pos="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%) scale(2)"
            color={borderColor}
            width="24px"
            height="24px"
          >
            <Spinner
              style={{
                borderWidth: "3px"
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const ChatAttachmentPreview = memo(_ChatAttachmentPreview)
export default ChatAttachmentPreview